import { DEG2RAD } from '../../utils.js';
import { WeaponMode } from '../../weapons/WeaponSystem.js';

/**
 * FT-103 — Air-to-Air Engagement.
 *
 * Weapons employment against airborne targets, which on an A-10 means
 * self-defence rather than dogfighting: no BFM, no guns jinking, just the two
 * things the jet can actually do to another aircraft. One Mi-8 killed with the
 * GAU-8, then a second, twelve kilometres due north, killed with a Sidewinder.
 *
 * ── Why the geometry is what it is ──────────────────────────────────────────
 * Both engagement areas sit in the open lowland west of the mountains — the
 * heightmap runs 70-260 m across the whole route, with nothing within 3 km of
 * either area above 260 m. That matters more here than in FT-102: the targets
 * fly at 250 m AGL and the player will be manoeuvring at their level with their
 * eyes outside the cockpit, so the ground has to be forgiving.
 *
 * The player spawns 15 km south-west of the first contact on heading 295, which
 * puts it on the nose. The second leg is 12 km due north, so "turn north" is
 * literally the instruction. Leg one is the longer of the two because it has to
 * carry the air-to-air gunnery brief, which is the bulk of the lesson: costed
 * against the authored durations the script runs about 63 seconds, leaving five
 * kilometres to close. Leg two needs less and gets less.
 *
 * ── Why the targets move, and what that costs ───────────────────────────────
 * A Hip in `explore` picks random waypoints anywhere in the world and sets off
 * at 55 m/s, so neither contact stays where it was put — over a leg one can
 * drift two or three kilometres in any direction. That is realistic enough and
 * makes it a genuine moving target, but it breaks a fixed HUD marker outright,
 * which is why both markers carry `track` and follow the live entity instead
 * (see TrainingMission._syncTrackedWaypoint). Everything in this file that
 * keys off `waypointRange` or `waypointHeadingError` — every hint, both
 * closure steps, the range readout on the objective row — is therefore reading
 * a live bearing to the bandit rather than to a patch of sky.
 *
 * The same drift is why each contact is spawned on the objective that calls
 * it, not at launch: it bounds how far it can wander before the player gets
 * there, and keeps the second contact off the HUD during the gun phase.
 *
 * ── The gunsight, which is the real lesson ──────────────────────────────────
 * CockpitHUD draws the CCIP pipper only when the gun line actually intersects
 * terrain within 3 km, and the air-to-air funnel the rest of the time. So the
 * sight the player gets depends on their geometry: come in roughly level with
 * the Hip and the funnel is up, which is the correct reference; dive steeply on
 * it and the ground pipper takes over and quietly shows where the rounds would
 * hit the dirt, several hundred metres past the target. Hence the briefing's
 * insistence on getting co-altitude first — it is a sight-picture problem, not
 * a style note.
 *
 * Voice-over: same convention as FT-101/FT-102 — recordings live under
 * public/assets/sound/voice/airToAir/ as `<audio>_captions`, `<audio>_complete`
 * and `<audio>_hint_<id>`. None are cut yet, so the authored caption
 * `duration`s are the literal on-screen times rather than relative weights
 * (see training/CaptionPlayer.js), which is what the pacing above is costed in.
 */

// ── Engagement areas ────────────────────────────────────────────────────────
// Flat open country; `agl:250` is the Hip's own cruise altitude, so it spawns
// already trimmed rather than climbing or diving onto profile.
const GUN_BANDIT = { x: 2000, z: 9000 };
const MISSILE_BANDIT = { x: 2000, z: -3000 };   // 12 km due north of the first

const GUN_ID = 'ftaGunBandit';
const MISSILE_ID = 'ftaMissileBandit';

// Both contacts are single Mi-8s. `spawnHelicopter` has no `state` hook — every
// helicopter flies `explore` — so there is no stationary option here even if one
// were wanted, which is the premise of the lesson anyway.
const gunBandit = [
  { type: 'mi8', id: GUN_ID, x: GUN_BANDIT.x, y: 'agl:250', z: GUN_BANDIT.z, heading: 180, initialHealth: 100 },
];
const missileBandit = [
  { type: 'mi8', id: MISSILE_ID, x: MISSILE_BANDIT.x, y: 'agl:250', z: MISSILE_BANDIT.z, heading: 0, initialHealth: 100 },
];

// Marker volume. Both waypoints are `guide` + `track`, so nothing is captured —
// these only size the HUD marker's ring.
const BANDIT_MARK = { radius: 500, vertical: 800 };

const weaponOf = (c) => c.app?.weaponSystem ?? null;

/**
 * The live entity for a training id, or null once it is down or pruned. Same
 * three-flag test as TrainingMission._targetsDown, deliberately — these feed
 * the "don't strand the player on a dead target" escapes below, and if they
 * disagreed with the `destroy` objectives the escapes would open a beat early
 * or a beat late.
 */
function bandit(c, id) {
  const e = c.enemyManager?.entities.find((x) => x.trainingId === id);
  return !e || e.dead || e.destroyed || e.flight?.structuralFailure ? null : e;
}

// ── Shared hints ────────────────────────────────────────────────────────────

// Flying into the ground while staring at a target is the one way to lose the
// airframe here, and both engagements happen at the Hip's altitude, so this is
// armed short and uncapped exactly as FT-102's is.
function pullUpHint() {
  return {
    id: 'pullUp',
    when: (c) => c.airborne && c.altitudeAGL < 120 && c.verticalSpeed < -8,
    after: 1.5, cooldown: 7, max: 99,
    audio: 'pullUpHint_pullUp',
    caption: 'Altitude, Hawg One — get the nose up.',
  };
}

// Lost the bandit astern. Because the marker tracks him, this is a true
// "he's behind you" and not a stale bearing to where he used to be.
function reacquireHint() {
  return {
    id: 'reacquire',
    when: (c) => Math.abs(c.waypointHeadingError) > 75 * DEG2RAD,
    after: 8, cooldown: 16,
    audio: 'reacquireHint_reacquire',
    caption: "He's off your nose — come round and put the marker back on the glass.",
  };
}

export default {
  id: 'airToAir',
  title: 'FT-103 · AIR-TO-AIR ENGAGEMENT',

  objectives: [
    // ── 1. Intro ────────────────────────────────────────────────────────────
    {
      id: 'intro',
      audio: '01_intro',
      type: 'info',
      objective: '',
      audioCaptions: [
        { caption: "Hawg One, instructor's up. Air-to-air today.", duration: 3 },
        { caption: "We're not teaching you to dogfight — we're showing you the two weapons that work against something in the air.", duration: 8 },
        { caption: 'Two contacts. One for the gun, one for the Sidewinder.', duration: 4 },
      ],
    },

    // ── 2. First contact ────────────────────────────────────────────────────
    // Puts the first Hip in the air. An `info` beat is never pre-empted by
    // _alreadyDone, so the spawn is guaranteed to happen exactly once.
    {
      id: 'contact',
      audio: '02_contact',
      type: 'info',
      objective: '',
      spawn: gunBandit,
      waypoint: { ...GUN_BANDIT, y: 0, track: GUN_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: 'Contact — single Hip, seven miles off the nose, low and slow.', duration: 5 },
        { caption: "He's marked on your HUD, and the marker follows him, so you won't lose him.", duration: 5.5 },
      ],
    },

    // ── 3. Select the gun ───────────────────────────────────────────────────
    {
      id: 'selectGun',
      audio: '03_selectGun',
      type: 'task',
      objective: 'Select the GAU-8 cannon  [T]',
      waypoint: { ...GUN_BANDIT, y: 0, track: GUN_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: 'Gun first. Tap T round to the cannon.', duration: 4 },
      ],
      complete: (c) => weaponOf(c)?.mode === WeaponMode.GAU8,
      hints: [
        { id: 'cycle', after: 9, cooldown: 14, caption: 'Weapon selector is T — cycle it round to the cannon.' },
      ],
    },

    // ── 4. Air-to-air gunnery brief ─────────────────────────────────────────
    // Runs its half-minute while the player is still three or four miles out.
    // Everything in it is a correction to what FT-102 taught them about the
    // gun, because against something in the air almost all of it changes.
    {
      id: 'gunBrief',
      audio: '04_gunBrief',
      type: 'info',
      objective: '',
      waypoint: { ...GUN_BANDIT, y: 0, track: GUN_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: 'Shooting at something in the air is nothing like shooting at a truck. Forget the ground pipper.', duration: 6 },
        { caption: "With sky behind him the sight gives you the funnel instead — those two curved lines. That's where your rounds are going.", duration: 7 },
        { caption: "So don't aim at him. Pull lead until he sits inside the funnel, then shoot.", duration: 5.5 },
        { caption: 'Get down to his level and come in from behind. If you dive on him the ground sight comes back — you want the funnel.', duration: 8 },
      ],
    },

    // ── 5. Get in behind him ────────────────────────────────────────────────
    // A real firing position, not a hoop: inside a mile with the bandit on the
    // nose. The `bandit() === null` escape is load-bearing — a player who
    // opened fire early and killed him on the way in must not be left chasing
    // the marker to a wreck.
    {
      id: 'getBehind',
      audio: '05_getBehind',
      type: 'task',
      objective: 'Close in behind the contact',
      waypoint: { ...GUN_BANDIT, y: 0, track: GUN_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: 'Bring it down to his altitude and work in behind him. Inside a mile.', duration: 5 },
        { caption: "And pull the power back — he's doing a hundred knots and you're doing three hundred. Close too fast and you'll go straight past him.", duration: 8 },
      ],
      complete: (c) => bandit(c, GUN_ID) === null
        || (c.waypointRange < 1600 && Math.abs(c.waypointHeadingError) < 25 * DEG2RAD),
      hints: [
        pullUpHint(),
        reacquireHint(),
        {
          id: 'overtake',
          when: (c) => c.waypointRange < 2500 && c.airspeedKts > 300,
          after: 4, cooldown: 12,
          caption: "You're closing far too fast — throttle back or you'll overshoot him.",
        },
      ],
    },

    // ── 6. Gun kill ─────────────────────────────────────────────────────────
    {
      id: 'gunKill',
      audio: '06_gunKill',
      type: 'destroy',
      objective: 'Destroy the contact with the gun',
      targets: [GUN_ID],
      waypoint: { ...GUN_BANDIT, y: 0, track: GUN_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: "That's a guns position. Lead him into the funnel and give me a short burst.", duration: 6 },
      ],
      hints: [
        pullUpHint(),
        reacquireHint(),
        {
          id: 'longShot',
          when: (c) => !!weaponOf(c)?.firing && c.waypointRange > 2000,
          after: 1.2, cooldown: 12,
          caption: "Too far out — you're wasting rounds. Get inside half a mile first.",
        },
      ],
      onCompleteCaptions: [
        { caption: "Splash. That's your first air-to-air kill with a cannon.", duration: 4 },
      ],
    },

    // ── 7. Second contact, to the north ─────────────────────────────────────
    // Puts the second Hip up two objectives ahead of the `destroy` that needs
    // it, and starts the twelve-kilometre run north.
    {
      id: 'turnNorth',
      audio: '07_turnNorth',
      type: 'info',
      objective: '',
      spawn: missileBandit,
      waypoint: { ...MISSILE_BANDIT, y: 0, track: MISSILE_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: 'Second contact, due north, eight miles. Come round onto north and run him down.', duration: 6.5 },
        { caption: "This one you're not going to gun. This one gets a Sidewinder.", duration: 5 },
      ],
    },

    // ── 8. Select the Sidewinder ────────────────────────────────────────────
    {
      id: 'selectAIM9',
      audio: '08_selectAIM9',
      type: 'task',
      objective: 'Select the AIM-9 Sidewinder  [T]',
      waypoint: { ...MISSILE_BANDIT, y: 0, track: MISSILE_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: 'Cycle the selector to the AIM-9.', duration: 3.5 },
      ],
      complete: (c) => weaponOf(c)?.mode === WeaponMode.AIM9,
      hints: [
        { id: 'cycle', after: 9, cooldown: 14, caption: "Tap T — the Sidewinder's the next station after the gun." },
      ],
    },

    // ── 9. Sidewinder brief ─────────────────────────────────────────────────
    // An `info`, so it always plays — the tone is the whole interface, and a
    // player who doesn't know what the growl means has nothing to work with.
    {
      id: 'aim9Brief',
      audio: '09_aim9Brief',
      type: 'info',
      objective: '',
      waypoint: { ...MISSILE_BANDIT, y: 0, track: MISSILE_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: "The Sidewinder is heat-seeking. Point it near something hot and it finds the heat on its own.", duration: 6.5 },
        { caption: "But it's a narrow little eye — fifteen degrees either side of the nose, and two miles. Outside that it sees nothing.", duration: 8 },
        { caption: "Listen for it. That low growl is the seeker searching. When it finds him the tone goes hard and steady and the reticle jumps onto him.", duration: 9 },
      ],
    },

    // ── 10. Acquire ─────────────────────────────────────────────────────────
    // Gated on the seeker actually holding *our* bandit rather than on range:
    // range alone would clear while the nose was pointed somewhere else, and
    // the lock tone is exactly the cue the brief just told them to wait for.
    {
      id: 'tone',
      audio: '10_tone',
      type: 'task',
      objective: 'Get a lock tone on the contact',
      waypoint: { ...MISSILE_BANDIT, y: 0, track: MISSILE_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: 'Put him on the nose and run in until you get the tone.', duration: 4.5 },
      ],
      complete: (c) => bandit(c, MISSILE_ID) === null
        || weaponOf(c)?.trackedTarget?.trainingId === MISSILE_ID,
      hints: [
        pullUpHint(),
        reacquireHint(),
        {
          id: 'closer',
          when: (c) => c.waypointRange > 3200,
          after: 12, cooldown: 18,
          caption: "Still just a growl — you're outside two miles. Keep running him down.",
        },
        {
          id: 'nose',
          when: (c) => c.waypointRange < 3200 && Math.abs(c.waypointHeadingError) > 15 * DEG2RAD,
          after: 4, cooldown: 10,
          caption: "You're in range but he's off to the side. Walk the nose onto him.",
        },
      ],
    },

    // ── 11. Missile kill ────────────────────────────────────────────────────
    // `destroy` rather than "fire a Sidewinder", so a player who somehow spends
    // both rounds without a kill can still finish on the gun instead of sitting
    // over a live target with an objective that can never clear.
    {
      id: 'missileKill',
      audio: '11_missileKill',
      type: 'destroy',
      objective: 'Destroy the contact',
      targets: [MISSILE_ID],
      waypoint: { ...MISSILE_BANDIT, y: 0, track: MISSILE_ID, label: 'BANDIT', guide: true, ...BANDIT_MARK },
      audioCaptions: [
        { caption: "That's your tone. Fire!", duration: 3 },
        { caption: "Once it's off the rail it's on its own — you don't have to follow it in. Ease off and watch.", duration: 6 },
      ],
      hints: [
        pullUpHint(),
        reacquireHint(),
        {
          id: 'shoot',
          when: (c) => weaponOf(c)?.trackedTarget?.trainingId === MISSILE_ID,
          after: 6, cooldown: 12,
          caption: "You've got the tone — left click and send it.",
        },
        {
          id: 'reload',
          when: (c) => weaponOf(c)?.aim9MissilesRemaining === 0,
          after: 3, cooldown: 20,
          caption: "Rails are empty. Switch back to the gun and finish him with that.",
        },
      ],
    },

    // ── 12. Debrief ─────────────────────────────────────────────────────────
    {
      id: 'debrief',
      audio: '12_debrief',
      type: 'info',
      objective: '',
      audioCaptions: [
        { caption: "Both contacts down — one with the gun, one with the missile.", duration: 4.5 },
        { caption: 'Air-to-air complete. Nice work, Hawg One.', duration: 4 },
      ],
    },
  ],
};
